import React from 'react';

import { useIntl } from 'react-intl';

import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { useParking } from 'src/providers/ParkingProvider';
import { userMessages } from 'src/features/users/utils/messages';
import { useCreateMembershipCloudUser } from 'src/features/membershipCloudUsers/api/useCreateMembershipCloudUser';
import { LIST_MEMBERSHIP_CLOUD_USERS_RESOURCE } from 'src/features/membershipCloudUsers/api/useListMembershipCloudUsers';
import MembershipCloudUserFormModal from 'src/features/membershipCloudUsers/components/forms/MembershipCloudUserFormModal';
import { membershipUsersMessages } from 'src/features/membershipCloudUsers/utils/messages';
import { generalErrorMessages } from 'src/utils/messages';
import { LIST_INVITATIONS_RESOURCE } from 'src/features/users/api/useListInvitations';

interface CreateMembershipCloudUserFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    children?: React.ReactNode;
    membershipId: string;
    isInvitation?: boolean;
}

function CreateMembershipCloudUserFormModal<TData>({
    className,
    membershipId,
    isInvitation,
}: CreateMembershipCloudUserFormModalProps<TData>) {
    const { formatMessage } = useIntl();
    const { parkingId } = useParking();

    const { mutate, isPending } = useCreateMembershipCloudUser({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_MEMBERSHIP_CLOUD_USERS_RESOURCE] });
            void queryClient.invalidateQueries({ queryKey: [LIST_INVITATIONS_RESOURCE] });
            toast({
                title: formatMessage(userMessages.invitationCreateSuccess),
                description: formatMessage(membershipUsersMessages.membershipUserInvitationCreateSuccessDetails),
            });
        },
        onError: (error) => {
            const emailError = error.response?.data?.email;

            const emailErrorDescription = emailError?.includes('Invitation already sent!')
                ? userMessages.invitationAlreadySent
                : emailError?.includes('Invitation already accepted!')
                  ? userMessages.invitationAlreadyAccepted
                  : generalErrorMessages.somethingWentWrongRetry;

            toast({
                title: formatMessage(generalErrorMessages.somethingWentWrong),
                description: formatMessage(emailErrorDescription),
                variant: 'destructive',
            });
        },
    });

    const titleEntryTranslation = isInvitation ? userMessages.invitation : membershipUsersMessages.membershipUser;

    return (
        <MembershipCloudUserFormModal
            className={className}
            mutate={(values, onSuccess) => mutate({ ...values, parkingId, membershipId }, { onSuccess })}
            title={formatMessage(generalMessages.createEntry, {
                entry: formatMessage(titleEntryTranslation).toLowerCase(),
            })}
            icon={ICON_MAP.ADD}
            isPending={isPending}
            subtitle={formatMessage(userMessages.newUserDescription)}
        />
    );
}

export default CreateMembershipCloudUserFormModal;
